<template>
  <div class="w-100">
    <b-card no-body cols="12" class="p-5">
      <b-row cols="12" class="mb-3">
        <b-col cols="12">
          <h2 class="text-blue font-weight-bold mt-1">
            {{ $t('profile') }}
          </h2>
        </b-col>
      </b-row>

      <validation-observer ref="profileForm" v-slot="{ handleSubmit }">
        <b-form @submit.prevent="handleSubmit(submit)">
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group :label="$t('first_name')" label-for="first_name">
                <validation-provider
                    name="first name"
                    rules="required"
                    v-slot="{ errors }"
                >
                  <b-form-input
                      id="first_name"
                      v-model="form.firstName"
                      :state="errors.length === 0 ? null : false"
                  ></b-form-input>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group :label="$t('last_name')" label-for="last_name">
                <validation-provider
                    name="last name"
                    rules="required"
                    v-slot="{ errors }"
                >
                  <b-form-input
                      id="last_name"
                      v-model="form.lastName"
                      :state="errors.length === 0 ? null : false"
                  ></b-form-input>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group :label="$t('email')" label-for="email">
                <validation-provider
                    name="email"
                    rules="required|email"
                    v-slot="{ errors }"
                >
                  <b-form-input
                      id="email"
                      v-model="form.email"
                      type="email"
                      autocomplete="email"
                      :state="errors.length === 0 ? null : false"
                  ></b-form-input>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group :label="$t('old_password')" label-for="old_password">
                <validation-provider
                    name="old password"
                    rules="min:8"
                    v-slot="{ errors }"
                >
                  <b-input-group>
                    <b-form-input
                        id="old_password"
                        v-model="form.oldPassword"
                        :type="showOldPassword ? 'text' : 'password'"
                        :state="errors.length === 0 ? null : false"
                        placeholder="Enter old password"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button @click="showOldPassword = !showOldPassword" class="p-0 pl-2 pr-2" variant="secondary">
                        <b-icon-eye-slash-fill v-if="showOldPassword" />
                        <b-icon-eye-fill v-else />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group :label="$t('new_password')" label-for="new_password">
                <validation-provider
                    name="new password"
                    :rules="form.oldPassword ? 'min:8' : ''"
                    v-slot="{ errors }"
                    vid="new_password"
                >
                  <b-input-group>
                    <b-form-input
                        id="new_password"
                        v-model="form.newPassword"
                        :type="showNewPassword ? 'text' : 'password'"
                        :state="errors.length === 0 ? null : false"
                        placeholder="Enter new password"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button @click="showNewPassword = !showNewPassword" class="p-0 pl-2 pr-2" variant="secondary">
                        <b-icon-eye-slash-fill v-if="showNewPassword" />
                        <b-icon-eye-fill v-else />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <div class="text-right mt-3 d-flex flex-column button-width ml-auto">
            <b-button type="submit" variant="primary">
              Submit
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-card>
    <b-card>
      <b-card-title> {{ $t("two_factor") }}</b-card-title>
      <b-card-text> {{ $t("two_factor_description") }}</b-card-text>
      <b-button class="float-right" @click="openTwoFactorModal" variant="primary"> {{ $t("change_status") }} </b-button>
    </b-card>

    <b-modal v-model="twoFactor" style="padding-top: 20vh;" scrollable size="md" hide-header no-close-on-backdrop hide-footer >
      <div>
        <two-factor @toggle-two-factor="toggleTwoFactor"></two-factor>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { extend } from 'vee-validate'
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, min, confirmed, email } from 'vee-validate/dist/rules'

extend('required', {
  ...required,
  message: (field) => `The ${field} field is required`
})

extend('min', {
  ...min,
  message: (field, params) => `The ${field} field must be at least ${params.length} characters`
})

extend('confirmed', {
  ...confirmed,
  message: (field) => `The ${field} field confirmation does not match`
})

extend('email', {
  ...email,
  message: 'The email field must be a valid email'
})

import {
  BButton,
  BCol,
  BForm,
  BRow,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BCard,
  BIconEyeFill,
  BIconEyeSlashFill
} from "bootstrap-vue";
import TwoFactor from "@/views/auth/TwoFactor";

export default {
  name: "Profile",

  components: {
    TwoFactor,
    BButton,
    BCol,
    BForm,
    BRow,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BCard,
    BIconEyeFill,
    BIconEyeSlashFill,
    ValidationProvider,
    ValidationObserver
  },

  data() {
    return {
      changePassword: false,
      showOldPassword: false,
      showNewPassword: false,
      form: {
        firstName: '',
        lastName: '',
        email: '',
        oldPassword: '',
        newPassword: '',
      },
      twoFactor: false,
      is_twoFactor: null,
    }
  },

  created() {
    this.init();
    this.getTwoFactorInfo();
  },

  methods: {
    async refreshUser() {
      try {
        let user = this.$store.state.auth.user;
        if ( user ) {
          const { data } = await this.$http.post(`/getUserDataWithId/${user.id}`);

          if (data && data.value) {
            console.log(data.value)
            this.$store.commit("auth/SET_USER", data.value);
            return data.value;
          } else {
            console.warn("No user returned from backend");
            return user;
          }
        }
      } catch (err) {
        console.error("Failed to refresh user:", err);
        return this.$store.state.auth.user;
      }
    },

    async submit() {
      if(this.form.oldPassword && this.form.oldPassword.length < 8){
        this.showError(this.$t('password_length_error'));
        return;
      }
      this.$http.patch('profile/admin/edit/' + this.$store.state.auth.user.id, this.form).then(async (res) => {
        if(res.data.status === 204) {
          this.showSuccessMessage();
          await this.refreshUser();
        }
        else if(res.data.status === 401)
          this.showError(this.$t("wrong_password"))
        else
          this.showError('')

        this.$router.push('/')
      })
    },

    async init() {
      this.$http.post('profile/admin/show/' + this.$store.state.auth.user.id).then((res) => {
        this.form.firstName = res.data.value.name
        this.form.lastName = res.data.value.last_name
        this.form.email = res.data.value.email
      });
    },

    showError(message) {
      this.$swal({
        title: this.$t('error'),
        text: message,
        icon: 'error',
        customClass: {
          confirmButton: 'btn btn-primary',
        },
        buttonsStyling: false,
      })
    },

    showSuccessMessage() {
      this.$swal({
        position: 'center',
        icon: 'success',
        title: this.$t('edited_successfully'),
        showConfirmButton: false,
        timer: 1500,
        customClass: {
          confirmButton: 'btn btn-primary',
        },
        buttonsStyling: false,
      })
    },
    getTwoFactorInfo() {
      this.$http
          .post(`check-user-two-factor/${this.$store.state.auth.user.id}`, {
            "check_code": true,
          })
          .then((res) => {
            if (res.data.value.bool) {
              this.is_twoFactor = true
            }
          });
    },
    toggleTwoFactor() {
      this.twoFactor = !this.twoFactor;
      console.log(this.twoFactor)
      this.getTwoFactorInfo()
    },
    openTwoFactorModal() {
      this.twoFactor = true
    },

  }
}
</script>

<style scoped>
.main-card{
  max-width: 800px;
  width: 100%;
}

.button-width{
  max-width: fit-content;
}
</style>